import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Check, X } from "lucide-react";

const BELONGS = ["", "Borrower", "Co-borrower", "Company", "Property / Seller", "Bank"];

export function BulkUpload({ recordId, onDone }) {
  const [limits, setLimits] = useState({ max_file_mb: 25, max_files: 30 });
  const [belongs, setBelongs] = useState("");
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { api.get("/documents/upload-limits").then((r) => setLimits(r.data)).catch(() => {}); }, []);

  const start = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, limits.max_files);
    if (Array.from(fileList || []).length > limits.max_files) toast.warning(`Only the first ${limits.max_files} files are uploaded`);
    if (!files.length) return;
    const rows = files.map((f) => ({ name: f.name, size: f.size, status: f.size > limits.max_file_mb * 1048576 ? "too_large" : "queued", pct: 0 }));
    setItems(rows);
    for (let i = 0; i < files.length; i++) {
      if (rows[i].status === "too_large") continue;
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("belongs_to", belongs);
      rows[i].status = "uploading"; setItems([...rows]);
      try {
        const r = await api.post(`/cases/${recordId}/documents/incoming/upload`, fd, { onUploadProgress: (e) => { rows[i].pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0; setItems([...rows]); } });
        rows[i].status = r.data.duplicate ? "duplicate" : "done";
      } catch (e) { rows[i].status = "error"; rows[i].error = e?.response?.data?.detail || "Upload failed"; }
      setItems([...rows]);
    }
    onDone?.();
  };

  return (
    <div data-testid="bulk-upload" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); start(e.dataTransfer.files); }}
      className={`rounded-2xl border-2 border-dashed p-4 bg-white transition-colors ${drag ? "border-[#FF4802] bg-[#FF4802]/5" : "border-[#E2E2E2]"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="bulk-upload-input" onChange={(e) => { start(e.target.files); e.target.value = ""; }} />
        <button onClick={() => inputRef.current?.click()} data-testid="bulk-upload-btn" className="flex items-center gap-1.5 text-xs bg-[#FF4802] text-white rounded-lg px-3 py-2 font-medium hover:bg-[#e03e00]"><Upload className="w-3.5 h-3.5" /> Upload files</button>
        <span className="text-xs text-[#6B7280]">or drop them here · up to {limits.max_files} files, {limits.max_file_mb} MB each · each file is read by AI and filed when it's sure</span>
        <label className="ml-auto text-xs text-[#6B7280] flex items-center gap-1.5">Belongs to
          <select value={belongs} onChange={(e) => setBelongs(e.target.value)} data-testid="bulk-upload-belongs" className="border border-[#E2E2E2] rounded-md px-2 py-1 text-xs bg-white">
            {BELONGS.map((b) => <option key={b} value={b}>{b || "Let AI decide"}</option>)}
          </select>
        </label>
      </div>
      {items.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="bulk-upload-progress">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 text-xs" data-testid={`bulk-upload-row-${i}`}>
              {it.status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF4802]" />}
              {(it.status === "done" || it.status === "duplicate") && <Check className="w-3.5 h-3.5 text-[#159A50]" />}
              {(it.status === "error" || it.status === "too_large") && <X className="w-3.5 h-3.5 text-[#CA1212]" />}
              {it.status === "queued" && <span className="w-3.5 h-3.5 rounded-full border border-[#C5C4C4]" />}
              <span className="truncate text-[#333333]">{it.name}</span>
              <span className="text-[#6B7280]">{(it.size / 1048576).toFixed(1)} MB</span>
              {it.status === "uploading" && <span className="text-[#6B7280]">{it.pct}%</span>}
              {it.status === "duplicate" && <span className="text-[#159A50]">already received</span>}
              {it.status === "done" && <span className="text-[#159A50]">uploaded · in Incoming files</span>}
              {it.status === "too_large" && <span className="text-[#CA1212]">too large (limit {limits.max_file_mb} MB)</span>}
              {it.status === "error" && <span className="text-[#CA1212]">{it.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
